import { Button, Tooltip, useToggle } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useSalesTaxFilingDetails } from '../../../sales-tax/filing/dashboard/useSalesTaxFilingDetail';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { FilingConfirmationModal } from './components/FilingConfirmationModal';

export const ConfirmFilingTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate: refetchServiceRequest } = context;

  const completeFilingModalVisibility = useToggle();

  const { filing, refetchFiling } = useSalesTaxFilingDetails({
    filingId: serviceRequest?.salesTaxFilingId,
    corporationId: serviceRequest?.corporationId,
  });

  const completeFilingHandler = async () => {
    refetchFiling();
    refetchServiceRequest();
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Filing review" />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Filing review in progress"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Filing completed"
                status={task.status}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Filing review" />,
          },
        },

        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={completeFilingModalVisibility.on}
                >
                  Complete filing
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Complete filing and upload acknowledgement" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={completeFilingModalVisibility.on}
                >
                  Complete filing
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Complete filing and upload acknowledgement"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Filing completed"
                status={task.status}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <div className="flex items-center gap-4">
                <Tooltip
                  side="left"
                  content="Filing is overdue. Please open the filing to complete it"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={completeFilingModalVisibility.on}
                    disabled
                  >
                    Complete filing
                  </Button>
                </Tooltip>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Complete filing and upload acknowledgement" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      {filing && (
        <FilingConfirmationModal
          isOpen={completeFilingModalVisibility.visible}
          onClose={completeFilingModalVisibility.off}
          filing={filing}
          onComplete={completeFilingHandler}
          corporationId={serviceRequest.corporationId}
          companyId={serviceRequest.companyId}
        />
      )}
    </div>
  );
};
